// CLOSEOUT1 (docs/build-briefs/CLOSEOUT1-closeout-redesign.md): the steps of the "Where the money went" waterfall. Pure and
// DOM-free. Every number is a figure the closeout API already returns: the sales half is `netSales` (service-date basis), the
// money half is `revenueByCollectionDate` (collection-date basis). The only arithmetic is the Timing step, the difference of
// two returned figures, drawn only when they differ. Nothing here recomputes a total.

import { formatCents } from "./money.ts";

export interface WaterfallNetSales {
  grossSalesCents: number;
  discountCents: number;
  compCents: number;
  refundBeforeTaxCents: number;
  netSalesCents: number;
}

export interface WaterfallRevenue {
  grossBeforeTaxCents: number;
  cardPriceCents?: number;
  taxCents: number;
  tipsCents: number;
  totalCollectedCents: number;
  discountCount: number;
  compCount: number;
  refundCount: number;
}

export type WaterfallKind = "total" | "minus" | "plus";

export interface WaterfallStep {
  key: "gross" | "discounts" | "comps" | "refunds" | "net" | "timing" | "card_price" | "tax" | "tips" | "total";
  label: string;
  kind: WaterfallKind;
  /** The step's own figure, always the positive size of the move. */
  cents: number;
  /** Running level before and after this step (a total starts at zero). */
  from: number;
  to: number;
  /** "$52.00", "-$28.60" or "+$1.94", the sign words a person reads. */
  valueText: string;
}

export interface WaterfallModel {
  steps: WaterfallStep[];
  /** Steps with a zero figure that were not drawn, by label, for the footnote. */
  omittedLabels: string[];
  /** True when there is nothing to draw (no sales and nothing collected). */
  empty: boolean;
  takeaway: string;
  ariaLabel: string;
  footnote: string;
}

const MINUS_SIGN = "−";

function signed(kind: WaterfallKind, cents: number): string {
  if (kind === "total") return formatCents(cents);
  return `${kind === "minus" ? MINUS_SIGN : "+"}${formatCents(cents)}`;
}

function joinWords(words: string[]): string {
  if (words.length <= 1) return words.join("");
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1] as string}`;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

export function buildWaterfall(netSales: WaterfallNetSales, revenue: WaterfallRevenue): WaterfallModel {
  const steps: WaterfallStep[] = [];
  const omitted: string[] = [];
  let level = 0;
  const push = (
    key: WaterfallStep["key"],
    label: string,
    kind: WaterfallKind,
    cents: number,
    mustDraw: boolean,
    namedWhenZero = true,
  ): void => {
    if (cents === 0 && !mustDraw) {
      if (namedWhenZero) {
        omitted.push(label);
      }
      return;
    }
    const from = kind === "total" ? 0 : level;
    const to = kind === "total" ? cents : kind === "minus" ? level - cents : level + cents;
    steps.push({ key, label, kind, cents, from, to, valueText: signed(kind, cents) });
    level = to;
  };

  push("gross", "Gross sales", "total", netSales.grossSalesCents, true);
  push("discounts", "Discounts", "minus", netSales.discountCents, false);
  push("comps", "Comps", "minus", netSales.compCents, false);
  push("refunds", "Refunds", "minus", netSales.refundBeforeTaxCents, false);
  push("net", "Net sales", "total", netSales.netSalesCents, true);
  level = netSales.netSalesCents;
  // Timing: sales before tax is counted on the day it was collected, net sales on the day it was rung. They differ when a tab
  // is paid on another day than it was rung.
  const timing = revenue.grossBeforeTaxCents - netSales.netSalesCents;
  if (timing !== 0) {
    push("timing", "Paid on another day", timing > 0 ? "plus" : "minus", Math.abs(timing), true);
  }
  // Card price exists only under dual pricing; a location without it is not told it was $0.00.
  push("card_price", "Card price", "plus", revenue.cardPriceCents ?? 0, false, false);
  push("tax", "Tax", "plus", revenue.taxCents, false);
  push("tips", "Tips", "plus", revenue.tipsCents, false);
  push("total", "Total collected", "total", revenue.totalCollectedCents, true);

  const empty = netSales.grossSalesCents === 0 && revenue.totalCollectedCents === 0;
  const takeaway = empty
    ? "No sales or payments are recorded for this day yet."
    : `${formatCents(netSales.grossSalesCents)} of gross sales became ${formatCents(revenue.totalCollectedCents)} collected.`;

  const footParts: string[] = [];
  if (omitted.length > 0) {
    footParts.push(`${joinWords(omitted)} ${omitted.length === 1 ? "was" : "were"} $0.00, so ${omitted.length === 1 ? "it is" : "they are"} not drawn.`);
  }
  if (revenue.discountCount > 0) {
    footParts.push(
      `Discounts: ${plural(revenue.discountCount, "application", "applications")}` +
        (revenue.compCount > 0 ? `, ${plural(revenue.compCount, "comp", "comps")} among them.` : "."),
    );
  }
  if (timing !== 0) {
    footParts.push("Paid on another day: tabs rung up on a different day than they were paid.");
  }
  if (revenue.refundCount > 0) {
    footParts.push(`Refunds: ${plural(revenue.refundCount, "refund", "refunds")}.`);
  }

  const ariaSteps = steps.map((step) => {
    const verb = step.kind === "minus" ? "less " : step.kind === "plus" ? "plus " : "";
    return `${verb}${step.label.toLowerCase()} ${formatCents(step.cents)}`;
  });
  const ariaLabel = empty
    ? "Waterfall chart: no sales or payments recorded for this day."
    : `Waterfall chart: ${ariaSteps.join(", ")}.${omitted.length > 0 ? ` ${joinWords(omitted)} ${omitted.length === 1 ? "was" : "were"} $0.00.` : ""}`;

  return { steps, omittedLabels: omitted, empty, takeaway, ariaLabel, footnote: footParts.join(" ") };
}
